import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActionDropdown,
  ActionDropdownAction,
  Button,
  FilterChips,
  TableData,
} from '@/components';
import SideBarModal from '@/components/Modal/SideBarModal';
import {
  showCaughtErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import { Column, BUSINESS_UNIT_LABELS } from '@/types/common';
import {
  ALERT_TYPE_DESCRIPTIONS,
  ALERT_TYPE_LABELS,
  AlertAssignee,
  AlertType,
  createAlertAssignee,
  deleteAlertAssignee,
  getAlertAssignees,
  updateAlertAssignee,
} from '@/services/api/monitoring';
import { getUserEmail } from '@/utils';
import { formatDateTime } from '@/utils/format';
import { SettingsTabs } from './components/SettingsTabs';
import {
  AlertAssigneeFormValues,
  AlertAssigneeModal,
} from './components/AlertAssigneeModal';

// Every list the table can manage. Telemetry-stale is here too: the rows
// used to be inserted by hand in SQL, and one screen for all of them beats
// remembering which ones still are.
const MANAGED_TYPES: AlertType[] = [
  AlertType.RENT_SHUTDOWN_PENDING,
  AlertType.RENT_SHUTDOWN_DONE,
  AlertType.RENT_REPOSSESSION_CREATED,
  AlertType.RENT_REPOSSESSION_FAILED,
  AlertType.TELEMETRY_STALE,
];

const AlertAssigneesPage: React.FC = () => {
  const [rows, setRows] = useState<AlertAssignee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<AlertType | null>(null);
  const [openActionRow, setOpenActionRow] = useState<string | number | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<AlertAssignee | null>(null);
  const [deleting, setDeleting] = useState<AlertAssignee | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  // The row whose active flag is mid-PATCH, so a double click cannot fire two.
  const [togglingID, setTogglingID] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      setRows(await getAlertAssignees());
    } catch (error) {
      showCaughtErrorToast(error, 'Gagal memuat daftar mention');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(
    () => (typeFilter ? rows.filter((r) => r.alertType === typeFilter) : rows),
    [rows, typeFilter]
  );

  const countByType = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of rows) {
      counts.set(row.alertType, (counts.get(row.alertType) ?? 0) + 1);
    }
    return counts;
  }, [rows]);

  const submit = async (values: AlertAssigneeFormValues) => {
    setIsSaving(true);
    try {
      const actor = getUserEmail();
      // '' from the select means "no business unit" — the fallback scope.
      const body = { ...values, businessUnit: values.businessUnit || null };
      if (editing) {
        await updateAlertAssignee(editing.ID, { ...body, updatedBy: actor });
        showSuccessToast('Berhasil', 'Mention diperbarui');
      } else {
        await createAlertAssignee({ ...body, createdBy: actor });
        showSuccessToast('Berhasil', 'Mention ditambahkan');
      }
      setIsModalOpen(false);
      setEditing(null);
      await load();
    } catch (error) {
      showCaughtErrorToast(error, 'Gagal menyimpan mention');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleActive = async (row: AlertAssignee) => {
    if (togglingID) return;
    setTogglingID(row.ID);
    try {
      await updateAlertAssignee(row.ID, {
        isActive: !row.isActive,
        updatedBy: getUserEmail(),
      });
      // One flag flipped: patch the row rather than refetch the whole list.
      setRows((prev) =>
        prev.map((r) => (r.ID === row.ID ? { ...r, isActive: !r.isActive } : r))
      );
    } catch (error) {
      showCaughtErrorToast(error, 'Gagal mengubah status');
    } finally {
      setTogglingID(null);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    setIsSaving(true);
    try {
      await deleteAlertAssignee(deleting.ID);
      showSuccessToast('Berhasil', 'Mention dihapus');
      setDeleting(null);
      await load();
    } catch (error) {
      showCaughtErrorToast(error, 'Gagal menghapus mention');
    } finally {
      setIsSaving(false);
    }
  };

  const columns = useMemo<Column<AlertAssignee>[]>(
    () => [
      {
        key: 'alertType',
        title: 'Notifikasi',
        render: (value: string) => (
          <span className="text-[13px] text-gray-900">
            {ALERT_TYPE_LABELS[value] ?? value}
          </span>
        ),
      },
      {
        key: 'businessUnit',
        title: 'Business unit',
        render: (value: string | null) =>
          value ? (
            BUSINESS_UNIT_LABELS[value] || value
          ) : (
            <span className="text-gray-500">Semua (fallback)</span>
          ),
      },
      { key: 'slackDisplayName', title: 'Nama' },
      {
        key: 'slackUserID',
        title: 'Slack member ID',
        render: (value: string) => (
          <span className="font-mono text-[13px]">{value}</span>
        ),
      },
      {
        key: 'isActive',
        title: 'Status',
        render: (value: boolean) => (
          <span
            className={`inline-block px-2 py-1 text-xs rounded-full font-medium ${
              value
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            {value ? 'Aktif' : 'Nonaktif'}
          </span>
        ),
      },
      {
        key: 'updatedAt',
        title: 'Diubah',
        render: (_value, record) =>
          formatDateTime(record.updatedAt ?? record.createdAt),
      },
      {
        key: 'actions',
        title: '',
        width: '48px',
        render: (_value, record) => {
          const actions: ActionDropdownAction[] = [
            {
              key: 'edit',
              label: 'Ubah',
              icon: 'ri-pencil-line',
              onClick: () => {
                setEditing(record);
                setIsModalOpen(true);
                setOpenActionRow(null);
              },
            },
            {
              key: 'toggle',
              label: record.isActive ? 'Nonaktifkan' : 'Aktifkan',
              icon: record.isActive ? 'ri-pause-line' : 'ri-play-line',
              isLoading: togglingID === record.ID,
              onClick: () => {
                setOpenActionRow(null);
                toggleActive(record);
              },
              separateAfter: true,
            },
            {
              key: 'delete',
              label: 'Hapus',
              icon: 'ri-delete-bin-line',
              className: 'text-red-600',
              onClick: () => {
                setOpenActionRow(null);
                setDeleting(record);
              },
            },
          ];
          return (
            <ActionDropdown
              record={record}
              isOpen={openActionRow === record.ID}
              onToggle={() =>
                setOpenActionRow((prev) =>
                  prev === record.ID ? null : record.ID
                )
              }
              actions={actions}
              ariaLabel={`Aksi untuk ${record.slackDisplayName}`}
            />
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openActionRow, togglingID]
  );

  return (
    <div className="bg-white p-6 max-w-full mx-auto">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[24px] font-semibold text-gray-900 mb-1">
            Settings
          </h1>
          <p className="text-[14px] text-gray-600">
            Siapa yang di-mention di Slack pada tiap notifikasi eskalasi rent
            dan alert telemetry.
          </p>
        </div>
        <Button
          variant="primary"
          size="small"
          className="whitespace-nowrap flex-shrink-0"
          onClick={() => {
            setEditing(null);
            setIsModalOpen(true);
          }}
        >
          <i className="ri-add-line text-[12px] mr-1"></i> Tambah mention
        </Button>
      </div>
      <SettingsTabs active="slack-mentions" />

      <div className="mb-4">
        <FilterChips
          options={MANAGED_TYPES.map((type) => ({
            value: type,
            label: ALERT_TYPE_LABELS[type] ?? type,
            count: countByType.get(type) ?? 0,
          }))}
          value={typeFilter}
          onChange={setTypeFilter}
          ariaLabel="Filter jenis notifikasi"
        />
        {typeFilter && (
          <p className="mt-2 text-[13px] text-gray-500">
            {ALERT_TYPE_DESCRIPTIONS[typeFilter]}
          </p>
        )}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <TableData
          data={visible}
          columns={columns}
          loading={isLoading}
          size="small"
          emptyState={
            <div className="py-10 text-center text-[13px] text-gray-500">
              Belum ada yang di-mention
              {typeFilter ? ' untuk notifikasi ini' : ''}. Alert tetap terkirim
              ke channel, hanya tanpa tag.
            </div>
          }
        />
      </div>

      <AlertAssigneeModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditing(null);
        }}
        onSubmit={submit}
        isSaving={isSaving}
        existing={editing}
        alertTypes={MANAGED_TYPES}
        defaultAlertType={typeFilter ?? undefined}
      />

      <SideBarModal
        isOpen={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        width="sm"
        header={
          <h2 className="text-xl font-semibold text-gray-900">
            Hapus mention?
          </h2>
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              size="small"
              onClick={() => setDeleting(null)}
              disabled={isSaving}
            >
              Batal
            </Button>
            <Button
              variant="danger"
              size="small"
              onClick={remove}
              disabled={isSaving}
            >
              {isSaving ? 'Menghapus…' : 'Hapus'}
            </Button>
          </div>
        }
      >
        <p className="p-6 text-[14px] text-gray-700">
          <b>{deleting?.slackDisplayName}</b> tidak akan di-mention lagi pada{' '}
          {deleting ? ALERT_TYPE_LABELS[deleting.alertType] : ''}. Kalau cuma
          sementara, pilih Nonaktifkan saja.
        </p>
      </SideBarModal>
    </div>
  );
};

export default AlertAssigneesPage;
